import type { QueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useAuth } from '@/hooks/use-auth';
import { usePlatformIntegrations } from '@/hooks/usePlatformIntegrations';
import { usePlatformNeedsReconnect } from '@/hooks/usePlatformNeedsReconnect';
import { postService } from '@/services/data';
import { logger } from '@/utils/logger';

/** Terminal, whole-post failures. Kept narrow — used where "failed" must mean failed. */
export const FAILED_POST_STATUSES = ['failed', 'failed_connection_removed'] as const;

export const PARTIAL_POST_STATUSES = ['partially_published', 'partially_scheduled'] as const;

/**
 * Everything a user must be told about, which is a STRICTLY WIDER set than
 * FAILED_POST_STATUSES.
 *
 * `partially_published` means at least one platform genuinely failed (the edge
 * fn only assigns it when successCount is between 1 and results.length), and
 * `partially_scheduled` means a platform terminally failed while others are
 * still pending. Both carry a confirmed per-platform failure, and both were
 * previously absent from every aggregate surface: the attention banner, the
 * sidebar badge, and the Calendar Failed tab all reported zero while a post sat
 * half-delivered — and the Sent tab listed it as if it had gone out.
 *
 * Reporting "nothing needs attention" while holding proof of a failure is the
 * exact competitor behaviour this product exists to not do.
 */
export const NEEDS_ATTENTION_POST_STATUSES = [
  ...FAILED_POST_STATUSES,
  ...PARTIAL_POST_STATUSES,
] as const;

export interface NeedsAttention {
  failedCount: number;
  partialCount: number;
  attentionPostCount: number;
  reconnectCount: number;
  total: number;
  /** Identity of the failed posts — feeds the dismissal signature. */
  failedPostIds: string[];
  /** Identity of the flagged platforms — feeds the dismissal signature. */
  reconnectPlatforms: string[];
  isLoading: boolean;
  /** True when the failed-post fetch errored — consumers hide rather than showing a fabricated all-clear. */
  isError: boolean;
}

/**
 * One derived "needs attention" signal for the whole app — failed posts
 * (id-level identity over both failed statuses) + accounts needing
 * reconnection. Consumed by the sidebar accounts surface, the Calendar
 * banner, and (O4) the Calendar List Failed tab so every surface agrees.
 *
 * `failedCount` comes from an EXACT `countPosts` head-only query — NOT
 * `failedPostIds.length`. `failedPostIds` is capped at 500 (see
 * `PostService.getPostIdsByStatuses`), so at >500 lifetime failures the two
 * numbers would silently diverge (badge clamps at 500 while the Failed tab's
 * own `countPosts` total keeps counting) if the badge read off the id list.
 * Both queries share the same status set (`NEEDS_ATTENTION_POST_STATUSES`) so they
 * stay one universe, just two different payload shapes for two different
 * jobs — count vs identity (see `failedPostIds` below).
 *
 * isLoading couples ALL THREE sources: the failed-count query, the
 * failed-post-ids query, and usePlatformIntegrations' own multi-hop load
 * (CLAUDE.md "Integrations Loading Flash" rule) — reconnectCount reads 0
 * until integrations settle, so reporting ready before then would briefly
 * under-count.
 */
export function useNeedsAttention(): NeedsAttention {
  const { user } = useAuth();
  const userId = user?.id;

  const {
    data: failedCount = 0,
    isLoading: isFailedCountLoading,
    isError: isFailedCountError,
  } = useQuery({
    queryKey: ['needs-attention-failed-count', userId],
    queryFn: async () => {
      const res = await postService.countPosts({
        status: [...FAILED_POST_STATUSES],
        uncleared: true,
      });
      if (res.error) {
        // Never fabricate an all-clear: log the real failure and surface
        // isError so consumers hide instead of rendering "0 need attention".
        logger.error('[useNeedsAttention] failed-post count errored:', res.error);
        throw res.error;
      }
      return res.data ?? 0;
    },
    enabled: Boolean(userId),
    staleTime: 60 * 1000,
  });

  const {
    data: partialCount = 0,
    isLoading: isPartialCountLoading,
    isError: isPartialCountError,
  } = useQuery({
    queryKey: ['needs-attention-partial-count', userId],
    queryFn: async () => {
      const res = await postService.countPosts({
        status: [...PARTIAL_POST_STATUSES],
        uncleared: true,
      });
      if (res.error) {
        logger.error('[useNeedsAttention] partly-sent count errored:', res.error);
        throw res.error;
      }
      return res.data ?? 0;
    },
    enabled: Boolean(userId),
    staleTime: 60 * 1000,
  });

  // failedPostIds is NOT the count source (see above) — it exists purely to
  // feed the dismissal signature (NeedsAttentionBanner's "did the exact set
  // of failed posts change since the user dismissed" check), which needs
  // post IDENTITY, not a total. It intentionally samples only the newest 500
  // failures (PostService.getPostIdsByStatuses' `.limit(500)`) — a dismissal
  // heuristic that tolerates missing the identity of failures beyond the
  // 500th, not a count that needs to be exact.
  const {
    data: failedPostIds = [],
    isLoading: isFailedIdsLoading,
    isError: isFailedIdsError,
  } = useQuery({
    queryKey: ['needs-attention-failed', userId],
    queryFn: async () => {
      const res = await postService.getPostIdsByStatuses([...NEEDS_ATTENTION_POST_STATUSES]);
      if (res.error) {
        logger.error('[useNeedsAttention] failed-post fetch errored:', res.error);
        throw res.error;
      }
      return res.data ?? [];
    },
    enabled: Boolean(userId),
    staleTime: 60 * 1000,
  });

  const { isLoading: isIntegrationsLoading } = usePlatformIntegrations();
  const { items } = usePlatformNeedsReconnect();
  const reconnectPlatforms = useMemo(() => items.map((item) => item.platform), [items]);

  const attentionPostCount = failedCount + partialCount;

  return {
    failedCount,
    partialCount,
    attentionPostCount,
    reconnectCount: reconnectPlatforms.length,
    total: attentionPostCount + reconnectPlatforms.length,
    failedPostIds,
    reconnectPlatforms,
    isLoading:
      isFailedCountLoading || isPartialCountLoading || isFailedIdsLoading || isIntegrationsLoading,
    isError: isFailedCountError || isPartialCountError || isFailedIdsError,
  };
}

/**
 * Invalidate BOTH needs-attention queries (prefix-match, so it catches every
 * userId variant of each key) — call after any mutation that can change
 * which posts are failed (retry/reschedule, delete) so the sidebar badge,
 * NeedsAttentionBanner, and CalendarTabs' Failed count all refetch instead of
 * silently going stale until an unrelated remount or the 60s staleTime
 * elapses. No mutation site invalidated these before this fix — see the
 * O-polish review packet (badge-count angle, P1).
 */
export function invalidateNeedsAttention(queryClient: QueryClient): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['needs-attention-failed'] }),
    queryClient.invalidateQueries({ queryKey: ['needs-attention-failed-count'] }),
    queryClient.invalidateQueries({ queryKey: ['needs-attention-partial-count'] }),
  ]).then(() => undefined);
}
